import { useLocation, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Home, LayoutDashboard, Search } from "lucide-react";

const t = {
  en: {
    code: "404",
    title: "Page not found",
    desc: "The page you're looking for doesn't exist or has been moved.",
    requested: "Requested URL",
    home: "Back to home",
    back: "Go back",
    dashboard: "My dashboard",
    listings: "Browse listings",
    contact: "Contact support",
  },
  fr: {
    code: "404",
    title: "Page introuvable",
    desc: "La page que vous cherchez n'existe pas ou a été déplacée.",
    requested: "URL demandée",
    home: "Retour à l'accueil",
    back: "Page précédente",
    dashboard: "Mon tableau de bord",
    listings: "Voir les annonces",
    contact: "Contacter le support",
  },
};

export default function NotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const tr = t[(language as "en" | "fr") || "en"] ?? t.en;

  useEffect(() => {
    console.error("404 — non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-6">
      <Card className="max-w-xl w-full p-10 text-center shadow-xl">
        <p className="text-7xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
          {tr.code}
        </p>
        <h1 className="mt-4 text-2xl font-semibold">{tr.title}</h1>
        <p className="mt-2 text-muted-foreground">{tr.desc}</p>

        <div className="mt-6 rounded-md bg-muted/50 px-4 py-2 text-sm font-mono text-muted-foreground break-all">
          <span className="opacity-60">{tr.requested}: </span>
          {location.pathname}
        </div>

        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {tr.back}
          </Button>
          <Button asChild>
            <Link to="/">
              <Home className="mr-2 h-4 w-4" />
              {tr.home}
            </Link>
          </Button>
          {user && (
            <Button asChild variant="secondary">
              <Link to="/dashboard">
                <LayoutDashboard className="mr-2 h-4 w-4" />
                {tr.dashboard}
              </Link>
            </Button>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-4 justify-center text-sm">
          <Link to="/listings" className="text-primary hover:underline inline-flex items-center gap-1">
            <Search className="h-3.5 w-3.5" /> {tr.listings}
          </Link>
          <Link to="/contact" className="text-primary hover:underline">
            {tr.contact}
          </Link>
        </div>
      </Card>
    </div>
  );
}
